import React, { FC, ReactNode, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GeometryType } from "@evergis/api";
import { IconButton } from "@evergis/ui";

import { StyleIconBackground, StyleIconImage } from "../../styled";
import { StyleIcon } from "../../../../components/StyleIcon/StyleIcon";
import { FeatureCardContext } from "./FeatureCardContext";
import { setPreviousFeatureReference } from "../../../../ducks/showFeature/showFeature";
import { getExtractedSymbol } from "../../../../utils/style";
import { deserializeSymbol } from "../../../../react-sgis/symbols/SymbolRenderer/deserializeSymbol";
import { updateSymbolParameter } from "../../../../evergis/core/classification/updateSymbolParameter";
import { getPreviousFeatureReference } from "../../../../ducks/showFeature/selectors";
import { useShowFeatureCard } from "../../../Table/hooks/useShowFeatureCard";
import { useCloseFeatureCardDialog } from "../../../../hooks/useDialogs";
import { useFeatureSymbol } from "../../hooks/useFeatureSymbol";
import { isClusterSymbol, SvgPointSymbol } from "../../../../interfaces/style";
import { SGisPolygonSymbol } from "../../../../react-sgis/symbols/SGisPolygonSymbol";
import { RangeClass, UniqueClass } from "../../../../interfaces/classification";

export const FeatureCardIcon: FC<{ image?: ReactNode }> = ({ image }) => {
  const {
    attributes,
    itemsCount,
    featureLayer,
    layerInfo,
    handBookReference,
    geometryType,
    isEdit,
    toggleEditMode,
    setHandBookReference,
  } = useContext(FeatureCardContext);
  const dispatch = useDispatch();
  const previousFeatureReference = useSelector(getPreviousFeatureReference);

  const { layer: previousLayer, id: previousId } = previousFeatureReference || {};
  const { style, type, sourceType } = layerInfo || {};
  const showFeature = useShowFeatureCard(previousLayer);

  const symbolHasRasters = style && style.hasRasters();
  const symbolIsClassified = style && style.classificationManager.hasClassifications();

  const featureSymbol = useFeatureSymbol(featureLayer, attributes);
  const extracted = getExtractedSymbol(style?.symbol, true);
  const isCluster = isClusterSymbol(featureSymbol);
  const isClusterSingleObject = isCluster && itemsCount === 1;
  const currentSymbol = isClusterSingleObject
    ? (extracted as SGisPolygonSymbol)?.originalSymbol || (deserializeSymbol(extracted) as SvgPointSymbol)
    : style?.symbol;

  const newSymbol = style.classificationManager.reduce((symbol, classification) => {
    const key = classification.parameter;
    const parts = key.split(".");
    let value = parts.length > 1 ? style.symbol?.[parts[0]]?.[parts[1]] : style.symbol?.[key];

    if (!value || value.type !== "byAttribute") return symbol;

    const classifyParameter = style.classificationManager.find(({ parameter }) => parameter === key);
    const attribute = attributes.find(({ name }) => name === classifyParameter.attribute);

    if (!attribute) return symbol;

    value = classifyParameter.values?.find(val =>
      (val as UniqueClass).uniqueValue !== undefined
        ? (val as UniqueClass).uniqueValue === attribute.value
        : ((val as RangeClass).from === null || (val as RangeClass).from <= attribute.value) &&
          ((val as RangeClass).to === null || (val as RangeClass).to >= attribute.value),
    )?.parameterValue;

    if (value === undefined) return symbol;

    return updateSymbolParameter(symbol, key, value);
  }, currentSymbol);

  const openEditCloseDialog = useCloseFeatureCardDialog();

  const showPreviousFeature = useCallback(async () => {
    await showFeature(previousId);
    dispatch(setPreviousFeatureReference(previousFeatureReference?.previous));
    setHandBookReference(previousFeatureReference?.reference);
  }, [
    showFeature,
    previousId,
    dispatch,
    previousFeatureReference?.previous,
    previousFeatureReference?.reference,
    setHandBookReference,
  ]);

  const onBackClick = useCallback(async () => {
    if (handBookReference) {
      setHandBookReference(void 0);
    } else if (previousFeatureReference) {
      if (isEdit) {
        openEditCloseDialog([
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          () => {
            toggleEditMode();
            showPreviousFeature();
          },
        ]);
      } else {
        await showPreviousFeature();
      }
    }
  }, [
    handBookReference,
    previousFeatureReference,
    setHandBookReference,
    isEdit,
    openEditCloseDialog,
    toggleEditMode,
    showPreviousFeature,
  ]);

  return (
    <StyleIconBackground>
      {image ? (
        <StyleIconImage>{image}</StyleIconImage>
      ) : (
        !handBookReference &&
        !previousFeatureReference && (
          <StyleIcon
            symbol={newSymbol}
            style={isClusterSingleObject ? undefined : style}
            geometryType={geometryType as GeometryType}
            type={type}
            sourceType={sourceType}
            hasRasters={symbolHasRasters}
            hasClassification={isClusterSingleObject ? undefined : symbolIsClassified}
          />
        )
      )}
      {(handBookReference || previousFeatureReference) && <IconButton kind={"arrow_back"} onClick={onBackClick} />}
    </StyleIconBackground>
  );
};
